import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [searchParams] = useSearchParams();
  // An invitation link carries its code, so the field should already be filled.
  const [invite, setInvite] = useState(() => (searchParams.get("code") || "").trim().toUpperCase());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(name, email, password, invite);
      toast.success(isLogin ? "Welcome back" : "Account created");
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const inputCls = "mt-1.5 w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF2A4D] focus:ring-1 focus:ring-[#FF2A4D]";

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      <div className="hidden lg:flex flex-col justify-between w-[45%] p-12 relative overflow-hidden border-r border-white/5"
        style={{ backgroundImage: "linear-gradient(rgba(5,5,5,0.55),rgba(5,5,5,0.85)), url(https://images.pexels.com/photos/7605370/pexels-photo-7605370.jpeg)", backgroundSize: "cover", backgroundPosition: "center" }}>
        <Link to="/" className="relative z-10 flex items-center gap-2.5">
          <img src="/icon.jpg" alt="" className="w-9 h-9 rounded-lg" />
          <span className="font-display text-2xl font-black tracking-tighter">SONLYO</span>
        </Link>
        <div className="relative z-10">
          <h2 className="font-display text-4xl font-black leading-tight tracking-tighter">
            Music without limits.<br /><span className="text-[#FF2A4D]">Ad-free.</span>
          </h2>
          <p className="text-zinc-300 mt-4 max-w-sm">An invite-only platform. Listen to whatever you want, whenever you want.</p>
        </div>
        <div className="relative z-10 text-xs text-zinc-500 font-mono">sonlyo.com</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up">
          <h1 className="font-display text-3xl font-black tracking-tighter">{isLogin ? "Log in" : "Create account"}</h1>
          <p className="text-zinc-400 mt-2 text-sm">{isLogin ? "Access your music." : "You need an invite code."}</p>

          <div className="mt-8 space-y-4">
            {!isLogin && (
              <div>
                <label className="text-sm text-zinc-400">Name</label>
                <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} placeholder="Your name" />
              </div>
            )}
            <div>
              <label className="text-sm text-zinc-400">Email</label>
              <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} placeholder="tu@email.com" />
            </div>
            <div>
              <label className="text-sm text-zinc-400">Password</label>
              <input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={inputCls} placeholder="••••••••" />
            </div>
            {!isLogin && (
              <div>
                <label className="text-sm text-zinc-400 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-[#FF2A4D]" /> Invite code</label>
                <input data-testid="auth-invite-input" value={invite} onChange={(e) => setInvite(e.target.value)} required className={`${inputCls} font-mono uppercase`} placeholder="SNL-XXXXXX" />
              </div>
            )}
          </div>

          {error && <p data-testid="auth-error" className="mt-4 text-sm text-red-400">{error}</p>}

          <button data-testid="auth-submit-btn" type="submit" disabled={loading}
            className="mt-6 w-full bg-[#FF2A4D] hover:bg-[#E61C3D] text-white rounded-full py-3 font-semibold transition-colors flex items-center justify-center disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : isLogin ? "Log in" : "Sign up"}
          </button>

          <p className="mt-6 text-center text-sm text-zinc-400">
            {isLogin ? "Have an invite? " : "Already have an account? "}
            <Link data-testid="auth-switch-link" to={isLogin ? "/register" : "/login"} className="text-[#FF2A4D] hover:underline font-medium">
              {isLogin ? "Sign up" : "Log in"}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
